import { useEffect, useRef, useState } from 'react';
import { stockBalanceAPI, getApiErrorMessage } from '@matix/api-client';
import type { StockBalanceDto, StockBalanceItemDto } from '@matix/api-client';
import { useUI } from '../../store/ui';
import { toast } from '../../store/toast';
import { XIcon, SearchIcon } from '../../components/icons';
import { useLang } from '../../lib/language';

/**
 * Stock Balance report — the new POS equivalent of the legacy "Balances"
 * modal behind the balance-scale icon.
 *
 * Flow matches the legacy: type into one search box (item name or code), pick
 * a result, and the table below fills with on-hand quantity per warehouse.
 * Columns follow the legacy view (#, Store Code, Warehouse, Qty) plus Colour
 * and Size for configurable items — legacy swapped the warehouse name out to
 * make room for those, which left variant rows unattributable.
 */
export function StockBalanceModal() {
  const isOpen = useUI((s) => s.openModal === 'stockBalance');
  const closeModal = useUI((s) => s.closeModal);
  const { t: tr, lang } = useLang();

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<StockBalanceItemDto[]>([]);
  const [searching, setSearching] = useState(false);
  const [report, setReport] = useState<StockBalanceDto | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset whenever the modal is (re)opened so a stale item never greets the
  // next cashier, and focus the box so it's type-and-go.
  useEffect(() => {
    if (!isOpen) return;
    setTerm('');
    setResults([]);
    setReport(null);
    setSearching(false);
    setLoading(false);
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  // Debounced search. `cancelled` guards against an earlier, slower response
  // overwriting the results of a later keystroke.
  useEffect(() => {
    if (!isOpen) return;
    const q = term.trim();
    if (q.length < 1) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const id = window.setTimeout(() => {
      stockBalanceAPI
        .searchItems(q)
        .then((items) => { if (!cancelled) setResults(items); })
        .catch((err) => { if (!cancelled) toast(getApiErrorMessage(err), 'error'); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [term, isOpen]);

  if (!isOpen) return null;

  function selectItem(item: StockBalanceItemDto): void {
    setResults([]);
    setTerm(itemLabel(item, lang));
    setLoading(true);
    stockBalanceAPI
      .forItem(item.id)
      .then(setReport)
      .catch((err) => { toast(getApiErrorMessage(err), 'error'); setReport(null); })
      .finally(() => setLoading(false));
  }

  const showVariantCols = report?.is_configurable ?? false;
  const unit = (lang === 'ar' ? report?.unit_name_ar : report?.unit_name) || report?.unit_name;

  return (
    <div className="modal-overlay show" onClick={closeModal}>
      <div className="modal stock-balance-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}
          >
            ⚖
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {tr({ en: 'Stock balance', ar: 'أرصدة المخزون' })}
            </div>
            <div className="modal-header-name-ar">
              {tr({ en: 'On-hand quantity per warehouse', ar: 'الكمية المتاحة في كل مخزن' })}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={closeModal}
            aria-label={tr({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body">
          {/* Search — one box, matched against item name (En/Ar) or code. */}
          <div className="stock-balance-search">
            <SearchIcon size={16} />
            <input
              ref={inputRef}
              type="text"
              value={term}
              onChange={(e) => { setTerm(e.target.value); setReport(null); }}
              placeholder={tr({
                en: 'Search by item name or code…',
                ar: 'ابحث باسم الصنف أو الكود…',
              })}
            />
            {term && (
              <button
                type="button"
                className="stock-balance-clear"
                onClick={() => { setTerm(''); setResults([]); setReport(null); inputRef.current?.focus(); }}
                aria-label={tr({ en: 'Clear', ar: 'مسح' })}
              >
                <XIcon size={14} />
              </button>
            )}
          </div>

          {results.length > 0 && (
            <ul className="stock-balance-results">
              {results.map((item) => (
                <li key={item.id}>
                  <button type="button" onClick={() => selectItem(item)}>
                    <span className="sb-result-code">{item.code}</span>
                    <span className="sb-result-name">{itemLabel(item, lang)}</span>
                    {item.is_configurable && (
                      <span className="sb-result-tag">
                        {tr({ en: 'variants', ar: 'متغيرات' })}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {searching && results.length === 0 && term.trim() && (
            <div className="stock-balance-hint">{tr({ en: 'Searching…', ar: 'جارٍ البحث…' })}</div>
          )}
          {!searching && term.trim() && results.length === 0 && !report && (
            <div className="stock-balance-hint">
              {tr({ en: 'No items match that name or code.', ar: 'لا توجد أصناف مطابقة للاسم أو الكود.' })}
            </div>
          )}

          {loading && (
            <div className="stock-balance-hint">{tr({ en: 'Loading balances…', ar: 'جارٍ تحميل الأرصدة…' })}</div>
          )}

          {report && !loading && (
            <>
              <div className="stock-balance-meta">
                <strong>{itemLabel(report.item, lang)}</strong>
                <span className="sb-meta-sep">·</span>
                <span>{tr({ en: 'Code', ar: 'كود' })} {report.item.code}</span>
                {unit && (
                  <>
                    <span className="sb-meta-sep">·</span>
                    <span>{tr({ en: 'Unit', ar: 'الوحدة' })}: {unit}</span>
                  </>
                )}
                <span className="sb-meta-sep">·</span>
                <span>
                  {report.all_warehouses
                    ? tr({ en: 'All warehouses', ar: 'كل المخازن' })
                    : tr({ en: 'Your warehouses', ar: 'مخازنك' })}
                </span>
              </div>

              <div className="stock-balance-table-wrap">
                <table className="stock-balance-table">
                  <thead>
                    <tr>
                      <th className="sb-num">#</th>
                      <th>{tr({ en: 'Store code', ar: 'كود المخزن' })}</th>
                      <th>{tr({ en: 'Warehouse', ar: 'المخزن' })}</th>
                      {showVariantCols && <th>{tr({ en: 'Colour', ar: 'اللون' })}</th>}
                      {showVariantCols && <th>{tr({ en: 'Size', ar: 'المقاس' })}</th>}
                      <th className="sb-qty">{tr({ en: 'Quantity', ar: 'الكمية' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.length === 0 ? (
                      <tr>
                        <td colSpan={showVariantCols ? 6 : 4} className="sb-empty">
                          {tr({
                            en: 'No stock recorded for this item in your warehouses.',
                            ar: 'لا يوجد رصيد لهذا الصنف في مخازنك.',
                          })}
                        </td>
                      </tr>
                    ) : (
                      report.rows.map((row) => (
                        <tr key={`${row.store_id}-${row.color}-${row.size}-${row.serial_no}`}>
                          <td className="sb-num">{row.serial_no}</td>
                          <td>{row.store_code}</td>
                          <td>{(lang === 'ar' ? row.store_name_ar : row.store_name) || row.store_name}</td>
                          {showVariantCols && <td>{row.color || '—'}</td>}
                          {showVariantCols && <td>{row.size || '—'}</td>}
                          <td className={`sb-qty${row.balance <= 0 ? ' is-zero' : ''}`}>
                            {formatQty(row.balance)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {report.rows.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={showVariantCols ? 5 : 3}>{tr({ en: 'Total', ar: 'الإجمالي' })}</td>
                        <td className="sb-qty">{formatQty(report.total_balance)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}

          {!report && !loading && !term.trim() && (
            <div className="stock-balance-hint">
              {tr({
                en: 'Search for an item to see how much is on hand in each warehouse.',
                ar: 'ابحث عن صنف لعرض الكمية المتاحة في كل مخزن.',
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function itemLabel(item: StockBalanceItemDto, lang: string): string {
  return (lang === 'ar' ? item.name_ar : item.name) || item.name || item.name_ar || `#${item.id}`;
}

/**
 * Quantities carry up to 3 decimals (the legacy report rounds to 3), but a
 * whole number shouldn't render as "714.000" — trailing zeros are noise on a
 * count of pieces.
 */
function formatQty(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}
